import React, { useState } from "react";
import {
  LayoutDashboard, LineChart, Radar, ArrowUpRight, ArrowDownRight,
  CircleCheck, Radio, Building2,
} from "lucide-react";
import { Block } from "./FlowBlocks";
import {
  dashboardRoles, analyticsStats, departmentPerformance, trackingFlow, liveQueue,
} from "../../mock";

// ---- 4. Role-based dashboards (interactive tabs)
export const DashboardsBlock = ({ data, index }) => {
  const [active, setActive] = useState(0);
  const role = dashboardRoles[active];

  return (
    <Block id={data.id} index={index} icon={LayoutDashboard} title={data.title} tagline={data.tagline} desc={data.desc} bullets={data.bullets} flip>
      <div>
        <div className="flex flex-wrap gap-2">
          {dashboardRoles.map((r, i) => (
            <button
              key={r.role}
              onClick={() => setActive(i)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all duration-200 ${
                i === active
                  ? "bg-cyan-400 text-slate-950"
                  : "border border-white/10 bg-white/5 text-slate-400 hover:border-cyan-400/30 hover:text-white"
              }`}
            >
              {r.role}
            </button>
          ))}
        </div>

        <div className="mt-5 rounded-2xl border border-white/10 bg-black/30 p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-base font-bold text-white">{role.role}</div>
              <div className="text-xs text-cyan-300/80">{role.focus}</div>
            </div>
            <span className="flex items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-2.5 py-1 text-[10px] font-medium text-emerald-300">
              <span className="blink-dot h-1.5 w-1.5 rounded-full bg-emerald-400" /> Live
            </span>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3">
            {role.metrics.map((m) => (
              <div key={m.label} className="rounded-xl border border-white/5 bg-white/[0.03] p-3">
                <div className="font-display text-lg font-bold text-white sm:text-xl">{m.value}</div>
                <div className="mt-0.5 text-[10px] leading-tight text-slate-500">{m.label}</div>
                {m.trend && <div className="mt-1 text-[10px] font-medium text-emerald-400">{m.trend}</div>}
              </div>
            ))}
          </div>

          <ul className="mt-4 space-y-1.5">
            {role.highlights.map((h) => (
              <li key={h} className="flex items-center gap-2 text-xs text-slate-400">
                <CircleCheck className="h-3.5 w-3.5 text-cyan-400" /> {h}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Block>
  );
};

// ---- 5. Call analytics visual
export const AnalyticsBlock = ({ data, index }) => (
  <Block id={data.id} index={index} icon={LineChart} title={data.title} tagline={data.tagline} desc={data.desc} bullets={data.bullets}>
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {analyticsStats.map((s) => (
          <div key={s.label} className="rounded-xl border border-white/5 bg-white/[0.03] p-3.5">
            <div className="font-display text-lg font-bold text-white sm:text-xl">{s.value}</div>
            <div className="mt-0.5 text-[10px] leading-tight text-slate-500">{s.label}</div>
            <div className={`mt-1 flex items-center gap-1 text-[10px] font-semibold ${s.up ? "text-emerald-400" : "text-cyan-300"}`}>
              {s.up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />} {s.trend}
            </div>
          </div>
        ))}
      </div>

      {/* call volume line chart (SVG) */}
      <div className="mt-5 rounded-2xl border border-white/10 bg-black/30 p-5">
        <div className="mb-3 flex items-center justify-between text-xs">
          <span className="font-semibold text-slate-300">Call Volume Trend</span>
          <span className="flex items-center gap-3 text-slate-500">
            <span className="flex items-center gap-1"><span className="h-1.5 w-3 rounded-full bg-cyan-400" />Incoming</span>
            <span className="flex items-center gap-1"><span className="h-1.5 w-3 rounded-full bg-slate-500" />Answered</span>
          </span>
        </div>
        <svg viewBox="0 0 300 90" className="w-full">
          <polyline className="draw-line" points="0,70 40,60 80,64 120,45 160,50 200,30 240,36 300,18" fill="none" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" />
          <polyline className="draw-line" style={{ animationDelay: "0.4s" }} points="0,78 40,72 80,74 120,58 160,62 200,44 240,50 300,34" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <div className="mt-1 flex justify-between text-[9px] text-slate-600">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <span key={d}>{d}</span>)}
        </div>
      </div>

      {/* department performance */}
      <div className="mt-5 space-y-2.5">
        {departmentPerformance.map((d) => (
          <div key={d.dept} className="flex items-center gap-3">
            <span className="w-28 shrink-0 text-xs text-slate-400">{d.dept}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/5">
              <div className="h-full rounded-full bg-gradient-to-r from-cyan-500/60 to-cyan-300" style={{ width: `${d.pct}%` }} />
            </div>
            <span className="w-9 text-right font-display text-xs font-bold text-cyan-300">{d.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  </Block>
);

// ---- 6. Live appointment tracking visual
export const TrackingBlock = ({ data, index }) => (
  <Block id={data.id} index={index} icon={Radar} title={data.title} tagline={data.tagline} desc={data.desc} bullets={data.bullets} flip>
    <div>
      {/* flow */}
      <div className="flex items-center justify-between gap-1">
        {trackingFlow.map((f, i) => (
          <React.Fragment key={f}>
            <div className="flex flex-col items-center gap-1.5">
              <div className={`flex h-9 w-9 items-center justify-center rounded-full border text-[9px] font-bold ${
                i < 3 ? "border-cyan-400/40 bg-cyan-400/15 text-cyan-200" : "border-white/10 bg-white/5 text-slate-400"
              }`}>
                {i + 1}
              </div>
              <span className="text-[10px] font-medium text-slate-400">{f}</span>
            </div>
            {i < trackingFlow.length - 1 && <div className="mb-5 h-px flex-1 bg-white/10" />}
          </React.Fragment>
        ))}
      </div>

      {/* counters */}
      <div className="mt-6 grid grid-cols-4 gap-2.5 text-center">
        {[
          { v: "248", l: "Today" },
          { v: "482", l: "Upcoming" },
          { v: "912", l: "Booked slots" },
          { v: "36", l: "Cancelled" },
        ].map((s) => (
          <div key={s.l} className="rounded-xl border border-white/5 bg-white/[0.03] px-1 py-3">
            <div className="font-display text-lg font-bold text-white">{s.v}</div>
            <div className="text-[9px] text-slate-500">{s.l}</div>
          </div>
        ))}
      </div>

      {/* live queue */}
      <div className="mt-5 overflow-hidden rounded-2xl border border-white/10">
        <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.04] px-5 py-3">
          <span className="flex items-center gap-2 text-xs font-semibold text-slate-300">
            <Radio className="h-3.5 w-3.5 text-red-400" /> Live Queue
          </span>
          <span className="text-[10px] text-slate-500">Updated just now</span>
        </div>
        {liveQueue.map((q, i) => (
          <div key={q.name} className={`flex items-center justify-between px-5 py-3 ${i !== liveQueue.length - 1 ? "border-b border-white/5" : ""}`}>
            <div>
              <div className="text-sm font-medium text-white">{q.name}</div>
              <div className="flex items-center gap-1 text-[10px] text-slate-500"><Building2 className="h-3 w-3" />{q.dept}</div>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="text-[10px] text-slate-500">{q.wait}</span>
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                q.status === "Next" ? "bg-cyan-400 text-slate-950" : "border border-white/15 bg-white/5 text-slate-300"
              }`}>
                {q.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </Block>
);
